<template>
  <section class="final-timesheet" aria-label="Итоговый табель">
    <portal to="task-timesheet-export">
      <div class="sheet-header-actions">
        <button type="button" class="custom-btn-secondary" :disabled="loading || saving || stale || !entries.length" @click="exportSheet">
          <v-icon name="buttons/export" :size="12" /> Экспорт
        </button>
        <button type="button" class="sheet-help-toggle" :aria-expanded="showHelp" aria-controls="task-timesheet-help" aria-label="Как работать с табелем" @click="showHelp = !showHelp">
          <v-icon name="buttons/question_circle" :size="14" />
        </button>
      </div>
    </portal>
    <portal to="task-timesheet-actions">
      <button v-if="canEdit" type="button" class="sheet-edit" :disabled="saving" @click="toggleEditing">
        <v-icon :name="editing ? 'buttons/check' : 'buttons/edit'" :size="14" />
        {{ editing ? 'Завершить редактирование' : 'Редактировать табель' }}
      </button>
    </portal>

    <div class="sheet-toolbar">
      <div class="sheet-filters">
        <b-dropdown no-caret toggle-class="sheet-menu-toggle" variant="link" aria-label="Действия с табелем">
          <template #button-content><span class="sheet-menu-dots" aria-hidden="true">…</span><span class="sr-only">Действия с табелем</span></template>
          <b-dropdown-item :disabled="loading || saving || !filtersReady" @click="reload">Обновить отклики</b-dropdown-item>
          <b-dropdown-item :aria-expanded="showHelp" aria-controls="task-timesheet-help" @click="showHelp = !showHelp">{{ showHelp ? 'Скрыть пояснения' : 'Как работать с табелем' }}</b-dropdown-item>
        </b-dropdown>
        <b-dropdown @show="calendarYear = Number(month.slice(0, 4))" ref="filters" toggle-class="sheet-period-toggle" menu-class="sheet-filter-menu" variant="link">
          <template #button-content><span class="sheet-period-label">{{ periodLabel }}</span></template>
          <b-dropdown-form @submit.stop.prevent>
            <div class="sheet-calendar" aria-label="Выбор месяца">
              <div class="sheet-calendar-year">
                <button type="button" aria-label="Предыдущий год" :disabled="saving || calendarYear <= 2000" @click="calendarYear--">‹</button>
                <strong>{{ calendarYear }}</strong>
                <button type="button" aria-label="Следующий год" :disabled="saving || calendarYear >= 2100" @click="calendarYear++">›</button>
              </div>
              <div class="sheet-calendar-months">
                <button v-for="(name, index) in monthNames" :key="name" type="button" :disabled="saving"
                  :aria-pressed="month === monthValue(index)" :class="{active: month === monthValue(index)}" @click="selectMonth(index)">{{ name }}</button>
              </div>
            </div>
            <b-form-group label="Группа заявок" label-for="sheet-group">
              <b-form-select id="sheet-group" v-model.number="groupId" :options="groupOptions" :disabled="saving" @change="reload" />
            </b-form-group>
            <b-form-group label="Объект" class="mb-0">
              <location-select :value="locationId" :is-disabled="saving" @onSelect="selectLocation" />
            </b-form-group>
          </b-dropdown-form>
        </b-dropdown>
        <b-spinner v-if="loading" small label="Обновление табеля" class="text-muted" />
      </div>
      <div class="sheet-actions" aria-label="Статусы смен">
        <span v-for="status in statuses" :key="status" :title="status === 'attended' ? selectionBlockReason : ''">
          <button v-if="editing" type="button" class="sheet-action" :class="'status-' + status"
            :disabled="saving || loading || stale || !selectedIds.length || (status === 'attended' && !!selectionBlockReason)"
            @click="setAttendance(status)">
            <v-icon :name="statusIcons[status]" :color="statusColors[status]" :size="13" />{{ status === 'planned' ? 'Запланировать' : labels[status] }}
          </button>
          <span v-else class="sheet-action" :class="'status-' + status">
            <v-icon :name="statusIcons[status]" :color="statusColors[status]" :size="13" />{{ status === 'planned' ? 'Запланировать' : labels[status] }}
          </span>
        </span>
      </div>
    </div>

    <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>
    <div id="task-timesheet-help" class="sheet-help-collapse" :class="{'is-open': showHelp}" :aria-hidden="!showHelp">
      <div class="sheet-help-content">
        <div class="sheet-help">
          <p>Нажмите «Редактировать табель», выберите смены и задайте статус. Итоги учитывают только смены со статусом «Вышел».</p>
          <p><span class="response-accepted">✓</span> СМЗ откликнулся в Баланс Про.
            <span class="response-missing">✕</span> Без отклика перевод в «Вышел» недоступен.</p>
          <p>Отклики обновляются автоматически. Если у исполнителя несколько работ в один день, каждая смена отмечается отдельно.</p>
        </div>
      </div>
    </div>
    <div v-if="!filtersReady" class="sheet-empty-state">
      <v-icon name="buttons/table_block" :size="32" />
      <h5>Выберите табель</h5>
      <p>Укажите месяц и группу заявок или объект в фильтре выше.</p>
    </div>
    <div v-else-if="loading && !entries.length" class="sheet-empty-state"><b-spinner small /> Загрузка табеля…</div>
    <div v-else-if="!entries.length && !error" class="sheet-empty-state">За выбранный месяц нет назначенных исполнителей.</div>

    <template v-if="entries.length">
      <div v-if="editing" class="sheet-selection" aria-live="polite">
        <span>{{ selectedIds.length ? 'Выбрано смен: ' + selectedIds.length : 'Выберите смены в табеле' }}</span>
        <button type="button" :disabled="saving || loading || stale" @click="selectAll">Выбрать все</button>
        <button v-if="selectedIds.length" type="button" :disabled="saving" @click="selectedIds = []">Снять выделение</button>
        <label class="sheet-all-rate">Ставка для всех
          <input v-model="allRate" type="number" min="0" max="1000000000" step="0.01" placeholder="₽" :disabled="saving || loading || stale" @focus="rateFocused = true" @blur="rateFocused = false" @keyup.enter="applyAllRate">
        </label>
        <button type="button" :disabled="saving || loading || stale || allRate === ''" @click="applyAllRate">Применить</button>
      </div>
      <p v-if="editing && selectionBlockReason" class="sheet-block-reason" role="status">{{ selectionBlockReason }}</p>
      <div class="sheet-scroll" tabindex="0" role="region" aria-label="Смены исполнителей по дням месяца">
        <table class="sheet-table">
          <caption class="sr-only">Табель: {{ periodLabel }}. Итоги по сменам со статусом «Вышел».</caption>
          <thead>
            <tr class="sheet-counts" aria-hidden="true">
              <td class="sheet-number"></td><td class="sheet-worker"></td><td class="sheet-rate"></td>
              <td v-for="day in days" :key="day.date">{{ counts[day.date] ? counts[day.date] + ' чел.' : '' }}</td>
              <td class="sheet-total sheet-total-days"></td><td class="sheet-total sheet-total-units"></td><td class="sheet-total sheet-total-amount"></td>
            </tr>
            <tr>
              <th class="sheet-number" scope="col"><span class="sheet-heading">№</span></th>
              <th class="sheet-worker" scope="col"><span class="sheet-heading">Исполнители</span></th>
              <th class="sheet-rate" scope="col"><span class="sheet-heading">Ставка</span></th>
              <th v-for="day in days" :key="day.date" scope="col" :aria-label="day.date">
                <span class="sheet-heading sheet-day-heading"><span>{{ day.name }}</span><span>{{ day.number }}</span></span>
              </th>
              <th class="sheet-total sheet-total-days" scope="col"><span class="sheet-heading">Кол-во<br>дней</span></th>
              <th class="sheet-total sheet-total-units" scope="col"><span class="sheet-heading">Кол-во<br>работы</span></th>
              <th class="sheet-total sheet-total-amount" scope="col"><span class="sheet-heading">Сумма<br>исполнителю</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.id">
              <td class="sheet-number">{{ index + 1 }}</td>
              <th class="sheet-worker" scope="row" :title="row.name">{{ shortName(row.name) }}</th>
              <td class="sheet-rate">
                <input v-if="editing && rowEditable(row)" class="sheet-rate-input" type="number" min="0" max="1000000000" step="0.01"
                  :value="row.rates.length === 1 ? row.rates[0] : ''" :placeholder="row.rates.length > 1 ? 'Разные' : ''"
                  :aria-label="'Ставка: ' + row.name" :disabled="saving || loading || stale"
                  @focus="rateFocused = true" @blur="rateFocused = false" @change="saveRate(row, $event)" @keyup.enter="$event.target.blur()">
                <span v-else :title="row.rates.map(formatNumber).join(', ')">{{ row.rates.length === 1 ? formatNumber(row.rates[0]) : 'Разные' }}</span>
              </td>
              <td v-for="day in days" :key="day.date" class="sheet-day">
                <span v-if="!row.cells[day.date]" class="sheet-empty" />
                <button v-for="entry in row.cells[day.date] || []" :key="entry.id" type="button"
                  class="sheet-shift" :class="['status-' + entry.attendance_status, {'sheet-selected': selectedIds.includes(entry.id)}]"
                  :id="'sheet-shift-' + entry.id" :aria-label="entryTitle(entry)" :aria-pressed="selectedIds.includes(entry.id)"
                  :aria-disabled="!editing || !entry.can_edit || saving || loading || stale"
                  @click="toggleEntry(entry)">
                  <span>{{ entry.attendance_status === 'attended' ? formatNumber(entry.total_units) : '\u00a0' }}</span>
                  <span v-if="indicator(entry)" class="sheet-response" :class="indicator(entry).accepted ? 'response-accepted' : 'response-missing'"
                    aria-hidden="true">{{ indicator(entry).accepted ? '✓' : '✕' }}</span>
                </button>
                <b-popover v-for="entry in row.cells[day.date] || []" :key="'info-' + entry.id" :target="'sheet-shift-' + entry.id"
                  triggers="hover focus" placement="auto" boundary="viewport" :delay="{show: 120, hide: 180}" custom-class="timesheet-shift-popover">
                  <div class="sheet-shift-info">
                    <strong>{{ entry.worker_name }}</strong>
                    <p>{{ entry.date }} · Заявка №{{ entry.task_id }}</p>
                    <p>{{ labels[entry.attendance_status] }} · {{ employmentLabel(entry.employment_type) }}</p>
                    <p>{{ entry.work_name || 'Работы по заявке' }}</p>
                    <p>{{ shiftTime(entry.start_date) }} — {{ shiftTime(entry.end_date) }}</p>
                    <p>Количество: {{ formatNumber(entry.total_units) }} {{ unitLabel(entry.unit_code) }}</p>
                    <p>Ставка: {{ formatNumber(entry.price) }} ₽</p>
                    <p>Расходы: {{ formatNumber(Number(entry.road_price || 0) + Number(entry.meal_cost || 0) + Number(entry.other_cost || 0)) }} ₽; аванс: {{ formatNumber(entry.prepaid) }} ₽</p>
                    <p v-if="indicator(entry)" :class="indicator(entry).accepted ? 'response-accepted' : 'response-missing'">{{ indicator(entry).text }}</p>
                    <button v-if="entry.can_edit" type="button" class="custom-btn-secondary" :disabled="saving || loading || stale" @click="openWork(entry)">Редактировать</button>
                  </div>
                </b-popover>
              </td>
              <td class="sheet-total sheet-total-days">{{ row.days.size }}</td>
              <td class="sheet-total sheet-total-units">{{ formatNumber(row.units) }}</td>
              <td class="sheet-total sheet-total-amount">{{ formatNumber(row.amount) }}</td>
            </tr>
          </tbody>
          <tfoot><tr>
            <td :colspan="days.length + 3"><span class="sr-only">Итого по выходам</span></td>
            <td class="sheet-total sheet-total-days"><span>{{ totals.days }}</span></td>
            <td class="sheet-total sheet-total-units"><span>{{ formatNumber(totals.units) }}</span></td>
            <td class="sheet-total sheet-total-amount"><span>{{ formatNumber(totals.amount) }}</span></td>
          </tr></tfoot>
        </table>
      </div>
    </template>
    <div v-if="tasks.length" class="sheet-summary">
      <div>
        <h5>Итоговые расчёты</h5>
        <p v-for="field in summaryFields" :key="field.key">{{ field.label }}: <strong>{{ formatNumber(summary[field.key]) }} ₽</strong></p>
        <p class="sheet-summary-note">Расчёт по сменам со статусом «Вышел».</p>
      </div>
      <div class="sheet-payment">
        <button type="button" class="custom-btn-primary" :disabled="!canSubmit || saving || loading || stale || !!workEntry || allRate !== ''" @click="confirmPayment">На оплату</button>
        <p v-if="paymentBlockReason" class="sheet-summary-note">{{ paymentBlockReason }}</p>
      </div>
    </div>
    <task-timesheet-work-modal v-if="workEntry" :key="workEntry.id" :entry="workEntry" :saving="saving" :error="workError" @close="closeWork" @save="saveWork" />
  </section>
</template>

<script>
import api from '../../network/api';
import * as XLSX from 'xlsx';
import LocationSelect from '../Locations/LocationSelect.vue';
import TaskTimesheetWorkModal from './TaskTimesheetWorkModal.vue';
import unitStore from '../../utilites/unitStore';
import {attendanceLabels, attendanceBlockReason, attendanceRows, responseIndicator} from '../../utilites/taskAttendance';

export default {
  name: 'TaskFinalTimesheet',
  components: {LocationSelect, TaskTimesheetWorkModal},
  mixins: [api, unitStore],
  props: {groupOptions: {type: Array, default: () => [{value: 0, text: 'Все группы'}]}},
  data() {
    const now = new Date();
    return {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
      groupId: 0, locationId: 0, entries: [], tasks: [], summary: {},
      calendarYear: now.getFullYear(), monthNames: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
      allRate: '', rateFocused: false, workEntry: null, workError: '',
      summaryFields: [{key: 'customer_amount', label: 'Сумма с заказчика'}, {key: 'worker_amount', label: 'Сумма исполнителям'},
        {key: 'worker_amount_with_commission', label: 'Сумма исполнителям с учётом комиссии'}, {key: 'vat', label: 'НДС'}, {key: 'delta', label: 'Дельта'}], selectedIds: [], editing: false,
      loading: false, saving: false, stale: false, error: '', requestNumber: 0, refreshTimer: null, showHelp: false,
      labels: attendanceLabels, statuses: ['planned', 'attended', 'absent'],
      statusIcons: {planned: 'buttons/calendar', attended: 'buttons/circle_check', absent: 'buttons/circle_cross'},
      statusColors: {planned: '#426bff', attended: '#60c12a', absent: '#ff4056'},
    };
  },
  computed: {
    canSubmit() { return this.tasks.length > 0 && this.tasks.every(task => task.can_submit) && this.tasks.some(task => task.status_id === 1); },
    paymentBlockReason() {
      if (this.tasks.length && this.tasks.every(task => task.status_id === 2)) return 'Заявки уже на оплате.';
      if (this.tasks.some(task => !task.can_submit)) return 'Некоторые заявки закрыты для изменения или недоступны по правам.';
      return '';
    },
    periodLabel() {
      const [year, month] = this.month.split('-').map(Number);
      if (!year || !month) return 'Выберите месяц';
      const date = new Date(year, month - 1).toLocaleDateString('ru-RU', {month: 'long', year: 'numeric'}).replace(/ г\.$/, '');
      const group = this.groupOptions.find(option => Number(option.value) === this.groupId);
      const suffix = this.groupId && group ? group.text : this.locationId ? 'По объекту' : 'Выберите группу';
      return `${date.charAt(0).toUpperCase() + date.slice(1)} — ${suffix}`;
    },
    filtersReady() { return /^\d{4}-\d{2}$/.test(this.month) && (this.groupId > 0 || this.locationId > 0); },
    canEdit() { return this.entries.some(entry => entry.can_edit); },
    rows() { return attendanceRows(this.entries); },
    days() {
      if (!this.month) return [];
      const [year, month] = this.month.split('-').map(Number);
      const days = [];
      const names = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      for (let n = 1; n <= new Date(year, month, 0).getDate(); n++) {
        const weekday = new Date(year, month - 1, n).getDay();
        days.push({date: `${this.month}-${String(n).padStart(2, '0')}`, number: n, name: names[weekday], weekend: weekday === 0 || weekday === 6});
      }
      return days;
    },
    counts() {
      const counts = {};
      this.rows.forEach(row => Object.keys(row.cells).forEach(date => { counts[date] = (counts[date] || 0) + 1; }));
      return counts;
    },
    totals() {
      return this.rows.reduce((total, row) => ({days: total.days + row.days.size, units: total.units + row.units, amount: total.amount + row.amount}), {days: 0, units: 0, amount: 0});
    },
    selectionBlockReason() {
      const entry = this.entries.find(item => this.selectedIds.includes(item.id) && attendanceBlockReason(item, 'attended'));
      return entry ? `${entry.worker_name}, ${entry.date}: ${attendanceBlockReason(entry, 'attended')}` : '';
    },
  },
  mounted() {
    this.refreshTimer = setInterval(() => {
      if (this.filtersReady && !this.saving && !this.loading && !this.workEntry && !this.rateFocused && this.allRate === '' && !document.hidden) this.load(true);
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    this.requestNumber++;
  },
  methods: {
    indicator: responseIndicator,
    unitLabel(code) { const unit = this.getUnitByCode(code); return unit && unit.shortName || 'ед.'; },
    employmentLabel(type) { return {1: 'СМЗ', 2: 'ГПХ', 3: 'ФЛ', 4: 'ИП', 5: 'В штате'}[type] || 'Не указан'; },
    shiftTime(value) { return value ? new Date(value).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : '—'; },
    monthValue(index) { return `${this.calendarYear}-${String(index + 1).padStart(2, '0')}`; },
    selectMonth(index) { this.month = this.monthValue(index); this.reload(); },
    filterParams() { return new URLSearchParams({month: this.month, group_id: this.groupId, location_id: this.locationId}); },
    rowEntries(row) { return this.entries.filter(entry => entry.worker_id === row.id); },
    rowEditable(row) { return this.rowEntries(row).every(entry => entry.can_edit); },
    async saveRate(row, event) {
      const value = event.target.value;
      const success = await this.updateRates(this.rowEntries(row), value);
      if (!success) event.target.value = row.rates.length === 1 ? row.rates[0] : '';
    },
    async applyAllRate() {
      if (await this.updateRates(this.entries, this.allRate)) this.allRate = '';
    },
    async updateRates(entries, value) {
      if (!this.editing || this.saving || this.loading || this.stale) return false;
      const price = Number(value);
      if (value === '' || !Number.isFinite(price) || price < 0 || price > 1e9) { this.error = 'Укажите корректную ставку.'; return false; }
      if (!entries.length || entries.length > 5000 || entries.some(entry => !entry.can_edit)) {
        this.error = 'Для общей ставки все выбранные смены должны быть доступны для редактирования (не более 5000 смен).'; return false;
      }
      return this.saveChanges(entries.map(entry => ({id: entry.id, price})));
    },
    openWork(entry) {
      if (!entry.can_edit || this.saving || this.loading || this.stale) return;
      this.$root.$emit('bv::hide::popover');
      this.workError = '';
      this.workEntry = entry;
    },
    closeWork() { if (!this.saving) { this.workEntry = null; this.workError = ''; } },
    async saveWork(change) { if (await this.saveChanges([change], true)) this.workEntry = null; },
    async saveChanges(changes, modal = false) {
      if (this.saving || this.loading || this.stale) return false;
      this.saving = true; this.error = ''; this.workError = '';
      try {
        const result = await this.put('tasks/timesheet/work', {changes});
        if (!result || result.error) {
          const message = result && result.message || 'Не удалось сохранить выполненные работы.';
          if (modal) this.workError = message; else this.error = message;
          return false;
        }
        await this.load();
        return true;
      } finally { this.saving = false; }
    },
    async confirmPayment() {
      if (!this.canSubmit || this.saving || this.loading || this.stale || this.workEntry || this.allRate !== '') return;
      // Keep the exact reviewed scope while the confirmation is open.
      const taskIds = this.tasks.map(task => task.id);
      const params = this.filterParams();
      this.saving = true;
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(`Перевести заявки этого табеля (${taskIds.length}) в статус «На оплату»? Редактирование заявок будет закрыто. Суммы учитывают только смены «Вышел».`,
          {title: 'На оплату', okTitle: 'На оплату', cancelTitle: 'Отменить', centered: true});
        if (!confirmed) return;
        const result = await this.post(`tasks/timesheet/payment?${params}`, {task_ids: taskIds});
        if (!result || result.error) { this.error = result && result.message || 'Не удалось отправить заявки на оплату.'; return; }
        this.editing = false; this.selectedIds = [];
        await this.load();
      } finally { this.saving = false; }
    },
    formatNumber(value) { return Number(value || 0).toLocaleString('ru-RU', {maximumFractionDigits: 2}); },
    shortName(name) {
      const [surname, ...names] = (name || '').trim().split(/\s+/);
      return [surname, ...names.map(part => part.charAt(0) + '.')].join(' ');
    },
    exportSheet() {
      if (this.loading || this.saving || this.stale || !this.entries.length) return;
      const header = ['№', 'Исполнитель', 'Ставка', ...this.days.map(day => day.date), 'Кол-во дней', 'Кол-во работы', 'Сумма исполнителю'];
      const data = this.rows.map((row, index) => [index + 1, row.name, row.rates.length === 1 ? row.rates[0] : row.rates.join(', '),
        ...this.days.map(day => {
          const values = (row.cells[day.date] || []).map(entry => {
            const response = responseIndicator(entry);
            return entry.attendance_status === 'attended' ? entry.total_units : `${attendanceLabels[entry.attendance_status]}${response ? '. ' + response.text : ''}`;
          });
          return values.length === 1 ? values[0] : values.join('\n');
        }),
        row.days.size, row.units, row.amount]);
      const footer = ['Итого', '', '', ...this.days.map(() => ''), this.totals.days, this.totals.units, this.totals.amount];
      const sheet = XLSX.utils.aoa_to_sheet([header, ...data, footer, [], ...this.summaryFields.map(field => [field.label, Number(this.summary[field.key] || 0)])]);
      sheet['!cols'] = [{wch: 8}, {wch: 32}, {wch: 12}, ...this.days.map(() => ({wch: 18})), {wch: 14}, {wch: 16}, {wch: 22}];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Табель');
      XLSX.writeFile(workbook, `Табель_${this.month}.xlsx`);
    },
    entryTitle(entry) {
      const response = responseIndicator(entry);
      return `${entry.worker_name}, ${entry.date}, заявка №${entry.task_id}. ${attendanceLabels[entry.attendance_status]}. Количество: ${this.formatNumber(entry.total_units)}.${response ? ' ' + response.text : ''}`;
    },
    selectLocation(id) { this.locationId = Number(id) || 0; this.reload(); },
    reload() {
      this.entries = []; this.tasks = []; this.summary = {}; this.allRate = '';
      this.selectedIds = [];
      return this.load();
    },
    async load(quiet = false) {
      const request = ++this.requestNumber;
      if (!this.filtersReady) { this.loading = false; this.error = ''; return; }
      this.loading = true;
      if (!quiet) this.error = '';
      try {
        const params = this.filterParams();
        params.set('details', '1');
        const result = await this.get(`tasks/timesheet?${params}`);
        if (request !== this.requestNumber) return;
        if (!result || result.error || !result.data || !Array.isArray(result.data.entries) || !Array.isArray(result.data.tasks)) {
          this.stale = true;
          this.error = result && result.message || 'Не удалось загрузить табель. Повторите обновление.';
          return;
        }
        this.entries = result.data.entries;
        this.tasks = result.data.tasks;
        this.summary = result.data.summary || {};
        this.stale = false;
        if (quiet) this.error = '';
        this.selectedIds = this.selectedIds.filter(id => this.entries.some(entry => entry.id === id && entry.can_edit));
      } finally {
        if (request === this.requestNumber) this.loading = false;
      }
    },
    toggleEditing() { this.editing = !this.editing; this.selectedIds = []; this.allRate = ''; },
    toggleEntry(entry) {
      if (!this.editing || !entry.can_edit || this.saving || this.loading || this.stale) return;
      if (this.selectedIds.includes(entry.id)) this.selectedIds = this.selectedIds.filter(id => id !== entry.id);
      else if (this.selectedIds.length < 200) this.selectedIds = [...this.selectedIds, entry.id];
      else this.error = 'За один раз можно изменить не больше 200 смен.';
    },
    selectAll() {
      const editable = this.entries.filter(entry => entry.can_edit);
      this.selectedIds = editable.slice(0, 200).map(entry => entry.id);
      if (editable.length > 200) this.error = 'Выбраны первые 200 доступных смен. Остальные можно изменить следующим действием.';
    },
    async setAttendance(status) {
      if (!this.editing || this.saving || this.loading || this.stale || !this.selectedIds.length) return;
      const targets = this.entries.filter(entry => this.selectedIds.includes(entry.id));
      const blocked = targets.find(entry => attendanceBlockReason(entry, status));
      if (blocked) { this.error = attendanceBlockReason(blocked, status); return; }
      this.saving = true;
      this.error = '';
      try {
        const result = await this.put('tasks/timesheet/attendance', {task_worker_ids: this.selectedIds, status});
        if (!result || result.error) {
          const message = result && result.message || 'Не удалось сохранить статус. Повторите попытку.';
          await this.load(true);
          this.error = message;
          return;
        }
        this.selectedIds = [];
        await this.load();
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.final-timesheet { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 16px 0 24px; color: #303540; font-size: 14px; }
.sheet-toolbar, .sheet-filters, .sheet-actions { display: flex; align-items: center; gap: 8px; }
.sheet-toolbar { justify-content: space-between; flex-wrap: wrap; margin-bottom: 12px; }
.sheet-filters { min-width: 0; }
.sheet-actions { gap: 4px; margin-left: auto; }
.sheet-header-actions { display: flex; align-items: center; gap: 24px; margin-right: 8px; }
.sheet-help-toggle { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: 0; border-radius: 6px; background: #f7f8fa; color: #303540; }
.sheet-edit { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; color: #15956e; background: transparent; border: 0; font-size: 14px; }
.sheet-edit:hover { color: #107754; }
.sheet-edit:disabled { opacity: .5; }
.final-timesheet >>> .sheet-menu-toggle, .final-timesheet >>> .sheet-period-toggle { display: inline-flex; align-items: center; justify-content: center; height: 32px; padding: 0 10px; border: 1px solid #d9e0ec; border-radius: 6px; background: white; color: #303540; font-size: 14px; text-decoration: none; }
.final-timesheet >>> .sheet-menu-toggle { width: 28px; padding: 0; }
.sheet-menu-dots { font-size: 18px; line-height: 16px; padding-bottom: 8px; }
.final-timesheet >>> .sheet-period-toggle { gap: 12px; max-width: 370px; }
.sheet-period-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.final-timesheet >>> .sheet-filter-menu { min-width: 300px; padding: 4px 0; }
.sheet-action { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 26px; padding: 4px 8px; border: 0; border-radius: 4px; color: #303540; font-size: 12px; line-height: 18px; white-space: nowrap; }
.sheet-action.status-planned { background: #f0f4ff; }
.sheet-action.status-attended { background: #f5fdea; }
.sheet-action.status-absent { background: #fff1f1; }
.sheet-action >>> svg path { fill: currentColor; }
button.sheet-action:disabled { opacity: .5; cursor: not-allowed; }
.sheet-selection { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin: 12px 0; color: #757c86; font-size: 12px; }
.sheet-selection button { padding: 0; border: 0; background: none; color: #15956e; }
.sheet-selection button:disabled { opacity: .5; }
.sheet-block-reason { color: #bf3547; font-size: 12px; margin-bottom: 12px; }
.sheet-help-collapse { display: grid; grid-template-rows: 0fr; opacity: 0; visibility: hidden; transition: grid-template-rows 240ms ease, opacity 180ms ease, visibility 0s linear 240ms; }
.sheet-help-collapse.is-open { grid-template-rows: 1fr; opacity: 1; visibility: visible; transition-delay: 0s; }
.sheet-help-content { min-height: 0; overflow: hidden; }
.sheet-help { margin: 12px 0; padding: 12px 16px; background: #f7f9fb; border-radius: 6px; font-size: 13px; }
.sheet-help p { margin: 0 0 4px; }
.sheet-help p:last-child { margin: 0; }
.sheet-help .response-missing { margin-left: 12px; }
.sheet-empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px 16px; color: #7d8490; text-align: center; }
.sheet-empty-state h5, .sheet-empty-state p { margin: 0; }
.sheet-scroll { overflow: auto; max-height: 55vh; padding-bottom: 4px; scrollbar-width: thin; scrollbar-color: #929598 transparent; }
.sheet-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
.sheet-scroll::-webkit-scrollbar-thumb { background: #929598; border-radius: 4px; }
.sheet-scroll:focus-visible, .sheet-edit:focus-visible, .sheet-shift:focus-visible { outline: 2px solid #15956e; outline-offset: -2px; }
.sheet-table { border-collapse: separate; border-spacing: 0; table-layout: fixed; width: max-content; min-width: 100%; font-size: 14px; }
.sheet-table th, .sheet-table td { box-sizing: border-box; text-align: center; padding: 2px; width: 36px; min-width: 36px; max-width: 36px; background: white; }
.sheet-table thead th { position: sticky; top: 18px; z-index: 2; padding-bottom: 2px; font-size: 12px; font-weight: 500; }
.sheet-heading { display: flex; align-items: center; justify-content: center; min-height: 48px; border-radius: 6px; background: #f4f4f6; line-height: 20px; }
.sheet-day-heading { flex-direction: column; }
.sheet-counts td { position: sticky; top: 0; z-index: 2; height: 18px; padding: 0 2px; color: #303540; font-size: 8px; white-space: nowrap; }
.sheet-table .sheet-number { position: sticky; left: 0; width: 44px; min-width: 44px; max-width: 44px; z-index: 3; }
.sheet-table .sheet-worker { position: sticky; left: 44px; width: 196px; min-width: 196px; max-width: 196px; text-align: left; z-index: 3; }
.sheet-table tbody .sheet-worker { padding-left: 12px; font-weight: 400; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sheet-worker .sheet-heading { justify-content: flex-start; padding-left: 8px; }
.sheet-table .sheet-rate { position: sticky; left: 240px; width: 64px; min-width: 64px; max-width: 64px; z-index: 3; }
.sheet-table .sheet-total { position: sticky; z-index: 3; font-weight: 600; }
.sheet-table .sheet-total-days { right: 172px; width: 60px; min-width: 60px; max-width: 60px; }
.sheet-table .sheet-total-units { right: 104px; width: 68px; min-width: 68px; max-width: 68px; }
.sheet-table .sheet-total-amount { right: 0; width: 104px; min-width: 104px; max-width: 104px; }
.sheet-table thead .sheet-number, .sheet-table thead .sheet-worker, .sheet-table thead .sheet-rate, .sheet-table thead .sheet-total { z-index: 4; }
.sheet-table tbody .sheet-total { white-space: nowrap; }
.sheet-table tfoot .sheet-total { padding-top: 0; }
.sheet-table tfoot .sheet-total span { display: block; padding: 4px 0; background: #ebf6f0; border: 1px solid #c6eada; border-radius: 5px; }
.sheet-shift, .sheet-empty { position: relative; display: block; width: 100%; height: 30px; min-height: 30px; border-radius: 6px; border: 1px solid transparent; padding: 0 2px; margin: 0; font-size: 14px; line-height: 28px; }
.sheet-shift + .sheet-shift { margin-top: 4px; }
.sheet-empty { background: #f8f8f9; }
.sheet-shift { color: #20242d; cursor: pointer; }
.sheet-shift[aria-disabled="true"] { cursor: default; }
.sheet-selected { border-color: #15956e; box-shadow: inset 0 0 0 1px #15956e; }
.status-planned { background: #d5e2ff; }
.status-attended { background: #d6f7b4; }
.status-absent { background: #ffc9c7; }
.sheet-response { position: absolute; bottom: 1px; right: 2px; line-height: 12px; font-size: 11px; font-weight: 600; }
.response-accepted { color: #52ba29; }
.response-missing { color: #fa4255; }
.sheet-summary { display: flex; justify-content: space-between; gap: 24px; flex-wrap: wrap; margin: 28px 12px 0; }
.sheet-summary h5 { margin: 0 0 10px; font-size: 16px; font-weight: 600; }
.sheet-summary p { margin: 0 0 4px; line-height: 18px; }
.sheet-summary .sheet-summary-note { margin-top: 8px; color: #858b94; font-size: 12px; }
@media (prefers-reduced-motion: reduce) {
  .sheet-help-collapse { transition: none; }
}
@media (max-width: 1100px) {
  .sheet-table .sheet-worker { width: 154px; min-width: 154px; max-width: 154px; }
  .sheet-table .sheet-rate { left: 198px; }
  .final-timesheet >>> .sheet-period-toggle { max-width: 270px; }
}
@media (max-width: 767px) {
  .sheet-header-actions { gap: 4px; }
  .sheet-toolbar { align-items: flex-start; gap: 12px; }
  .sheet-actions { margin-left: 0; flex-wrap: wrap; }
  .sheet-edit { padding: 8px 4px; font-size: 12px; }
  .sheet-table .sheet-number { width: 32px; min-width: 32px; max-width: 32px; }
  .sheet-table .sheet-worker { left: 32px; width: 128px; min-width: 128px; max-width: 128px; }
  .sheet-table .sheet-rate, .sheet-table .sheet-total { position: static; }
  .sheet-table thead .sheet-rate, .sheet-table thead .sheet-total { position: sticky; left: auto; right: auto; z-index: 2; }
  .sheet-summary { margin: 24px 0 0; }
}
.sheet-rate-input { width: 100%; min-width: 0; padding: 5px 2px; border: 1px solid #d9e0ec; border-radius: 5px; text-align: center; -moz-appearance: textfield; }
.sheet-rate-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.sheet-all-rate { display: inline-flex; align-items: center; gap: 8px; margin: 0 0 0 auto; }
.sheet-all-rate input { width: 100px; padding: 5px 8px; border: 1px solid #d9e0ec; border-radius: 5px; }
.sheet-calendar { margin-bottom: 20px; }
.sheet-calendar-year { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.sheet-calendar-year button { border: 0; background: transparent; padding: 4px 12px; font-size: 22px; }
.sheet-calendar-months { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
.sheet-calendar-months button { border: 0; border-radius: 6px; padding: 8px; background: #f5f6f7; color: #303540; }
.sheet-calendar-months button:hover { background: #ebf6f0; }
.sheet-calendar-months button.active { background: #15956e; color: white; }
.sheet-payment { text-align: right; max-width: 260px; }
.sheet-shift-info { min-width: 240px; color: #303540; font-size: 13px; }
.sheet-shift-info p { margin: 6px 0; }
.sheet-shift-info button { margin-top: 8px; }
</style>



// WEBPACK FOOTER //
// src/components/Tasks/TaskFinalTimesheet.vue